import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';

import { IKnmAddressTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmAddressTypeSelectOption';
import {
  getCatalogKnmAddressTypesSelectOptions,
  getCatalogRiskCategoriesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { OBJECTS_BLOCK_ID, getObjectBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmFormRef,
  getKnmStatus,
  getKnmSupervisionTypesSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IRiskCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IRiskCategorySelectOption';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getUserRoleIsProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { KnmErpStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';

import { KnmErpObject } from './KnmErpObject';
import { IKnmErpFormObject } from '../interfaces/IKnmErpFormObject';
import { IKnmErpFormResult } from '../../KnmErpResultsModule/interfaces/IKnmErpFormResult';
import { IKnmErpFormChecklist } from '../../KnmErpChecklistsModule/interfaces/IKnmErpFormChecklist';
import { IKnmErpErvkFormObject } from '../interfaces/IKnmErpErvkFormObject';
import { KnmErpObjectErvk } from './KnmErpObjectErvk';
import { IKnmSupervisionTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';

interface IKnmErpObjectsProps {
  registryLegalAddressFns?: string;
  replaceAlertsVisible?: boolean;
  watchOnly?: boolean;
}

interface IKnmErpObjectsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmAddressTypesSelectOptions: IKnmAddressTypeSelectOption[];
  knmSupervisionTypesSelectOptions: IKnmSupervisionTypeSelectOption[] | undefined;
  riskCategoriesSelectOptions: IRiskCategorySelectOption[];
  settings: IAdminSetting[] | undefined;
  created: string | null | undefined;
  isUserProsecutor: boolean | undefined;
  knmStatusType: IKnmStatus | undefined;
}

const KnmErpObjectsWithoutStore = ({
  registryLegalAddressFns,
  watchOnly,
  replaceAlertsVisible,
  formRef,
  knmAddressTypesSelectOptions,
  knmSupervisionTypesSelectOptions,
  riskCategoriesSelectOptions,
  settings,
  created,
  isUserProsecutor,
  knmStatusType,
}: IKnmErpObjectsProps & IKnmErpObjectsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isUserERVKParamTables = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );

  const objectsFieldName = isUserERVKParamTables ? 'objectsErvk' : 'objects';
  const isChangeObjectsAvailableProsecutorCase = useMemo(() => {
    return (
      (isUserProsecutor && knmStatusType?.code !== KnmErpStatusCodesEnum.TYPE_COMPLETED) ||
      knmStatusType?.code !== KnmErpStatusCodesEnum.TYPE_CANT_BE_CARRY_OUT
    );
  }, [isUserProsecutor, knmStatusType]);

  const isObjectsEditAvailable = !watchOnly || isChangeObjectsAvailableProsecutorCase;

  const addKnmObject = useCallback(
    () => !watchOnly && push && push(objectsFieldName, { listId: uuidv4(), isNew: true }),
    [push, watchOnly],
  );

  const makeRemoveKnmObjectHandler = (index: number) => () => {
    if (!watchOnly && remove) {
      const currentValues = formRef.current.getState().values;
      const objectsArray = currentValues[objectsFieldName] || [];
      const objectToRemove = objectsArray[index];

      remove(objectsFieldName, index);

      const objectResults: IKnmErpFormResult[] | undefined = formRef?.current.getFieldState('objectsResults')?.value;

      objectResults?.forEach((result, objectResultIndex) => {
        const resultObjectId = result.object?.guid;

        if ((resultObjectId && resultObjectId === objectToRemove?.guid) || resultObjectId === objectToRemove?.listId) {
          remove('objectsResults', objectResultIndex);
        }
      });

      const checkLists: IKnmErpFormChecklist[] | undefined = formRef?.current.getFieldState('checklists')?.value;

      checkLists?.forEach((checklist, checkListIndex) => {
        const checkListObjectId = checklist.object?.guid;

        if (
          (checkListObjectId && checkListObjectId === objectToRemove?.guid) ||
          checkListObjectId === objectToRemove?.listId
        ) {
          remove('checklists', checkListIndex);
        }
      });
    }
  };

  return (
    <Row>
      <FieldBlock
        label="Объекты проведения КНМ"
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={watchOnly}
            onClick={addKnmObject}
          />
        }
        id={OBJECTS_BLOCK_ID}
      >
        <FieldArray name={objectsFieldName}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const fieldValue: IKnmErpFormObject | IKnmErpErvkFormObject | undefined = fields.value?.[index];

              if (!fieldValue || !fieldValue?.listId) {
                return null;
              }

              const id = getObjectBlockId(fieldValue?.listId || uuidv4());

              return isUserERVKParamTables ? (
                <KnmErpObjectErvk
                  key={id}
                  address={fieldValue.address}
                  autocomplete={fieldValue.autocomplete}
                  id={id}
                  index={index}
                  isNew={fieldValue.isNew}
                  name={name}
                  objectGuid={fieldValue.listId}
                  onRemove={makeRemoveKnmObjectHandler(index)}
                />
              ) : (
                <KnmErpObject
                  key={id}
                  formRef={formRef}
                  id={id}
                  index={index}
                  knmAddressTypesOptions={knmAddressTypesSelectOptions}
                  riskCategoriesSelectOptions={riskCategoriesSelectOptions}
                  name={name}
                  registryLegalAddressFns={registryLegalAddressFns}
                  replaceAlertsVisible={replaceAlertsVisible}
                  knmSupervisionTypesSelectOptions={knmSupervisionTypesSelectOptions}
                  watchOnly={!isObjectsEditAvailable}
                  onRemove={makeRemoveKnmObjectHandler(index)}
                  isNew={fieldValue.isNew}
                  address={fieldValue.address}
                  autocomplete={fieldValue.autocomplete}
                />
              );
            });
          }}
        </FieldArray>
      </FieldBlock>
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpObjectsStoreProps => ({
  formRef: getKnmFormRef(state),
  knmAddressTypesSelectOptions: getCatalogKnmAddressTypesSelectOptions(state),
  knmSupervisionTypesSelectOptions: getKnmSupervisionTypesSelectOptions(state),
  riskCategoriesSelectOptions: getCatalogRiskCategoriesSelectOptions(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  isUserProsecutor: getUserRoleIsProsecutor(state),
  knmStatusType: getKnmStatus(state),
});

export const KnmErpObjects = connect(mapStateToProps)(KnmErpObjectsWithoutStore);
